<template>
  <base-field :annotation="annotation" :scale="scale" :selected="selected" :editable="editable"
    @field-click="handleClick" @position-change="$emit('position-change', $event)">
    <div class="signature-display" :class="[
      { 'has-signature': !!value },
      `signee-${signeeType}`,
      `box-style-${boxStyle}`
    ]">
      <!-- Signature Image or Placeholder -->
      <div class="signature-content">
        <img v-if="value" :src="value" alt="Signature" class="signature-image" />
        <div v-else class="signature-placeholder">
          <i class="fa fa-pen-fancy"></i>
          <span>{{ editing ? 'Click to sign' : (annotation.label || annotation.fieldName || placeholderText) }}</span>
          <small v-if="signeeType" class="signee-badge">{{ signeeTypeLabel }}</small>
        </div>
      </div>

    </div>
  </base-field>
</template>

<script>
import Vue from 'vue';
import BaseField from './BaseField.vue';

export default {
  name: 'SignatureField',
  components: { BaseField },
  props: {
    annotation: {
      type: Object,
      required: true
    },
    value: {
      type: String,
      default: null
    },
    scale: {
      type: Number,
      default: 1
    },
    selected: {
      type: Boolean,
      default: false
    },
    editing: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    signeeType() {
      // signee_type is now at the top level of the annotation
      return this.annotation.signee_type || null;
    },
    boxStyle() {
      return this.annotation.properties?.boxStyle || 'border';
    },
    signeeTypeLabel() {
      const labels = {
        student: 'Student',
        guardian: 'Guardian',
        instructor: 'Instructor',
        staff: 'Staff'
      };
      return labels[this.signeeType] || '';
    },
    placeholderText() {
      if (this.signeeType) {
        return `${this.signeeTypeLabel} signature required`;
      }
      return 'Signature required';
    }
  },
  methods: {
    handleClick() {
      if (this.editing) {
        this.$emit('open-signature', this.annotation);
      } else {
        this.$emit('field-click', this.annotation);
      }
    }
  }
};
</script>

<style scoped>
.signature-display {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #fff;
  border-radius: 4px;
  box-sizing: border-box;
}

/* Box Styles */
.signature-display.box-style-border {
  border: 2px dashed #ccc;
}

.signature-display.box-style-underline {
  border: none;
  border-bottom: 2px solid #000;
}

.signature-display.box-style-none {
  border: 1px dashed rgba(0, 0, 0, 0.1);
}

/* Signee Type Color Coding */
.signature-display.signee-student {
  border-color: #3b82f6;
  /* Blue */
  background-color: rgba(59, 130, 246, 0.05);
}

.signature-display.signee-guardian {
  border-color: #10b981;
  /* Green */
  background-color: rgba(16, 185, 129, 0.05);
}

.signature-display.signee-instructor {
  border-color: #f59e0b;
  /* Orange */
  background-color: rgba(245, 158, 11, 0.05);
}

.signature-display.signee-staff {
  border-color: #8b5cf6;
  /* Purple */
  background-color: rgba(139, 92, 246, 0.05);
}

.signature-display.signee-admin {
  border-color: #ef4444;
  /* Red */
  background-color: rgba(239, 68, 68, 0.05);
}

.signature-display.has-signature {
  border-style: solid;
}

/* Content Area - must stay within annotation bounds */
.signature-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.signature-image {
  display: block;
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
}

.signature-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #718096;
  font-size: 11px;
  text-align: center;
  gap: 4px;
}

.signature-placeholder i {
  font-size: 20px;
  margin-bottom: 2px;
  opacity: 0.6;
}

.signature-placeholder span {
  font-weight: 500;
  line-height: 1.2;
}

.signee-badge {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

.signee-student .signee-badge {
  background-color: #3b82f6;
  color: white;
}

.signee-guardian .signee-badge {
  background-color: #10b981;
  color: white;
}

.signee-instructor .signee-badge {
  background-color: #f59e0b;
  color: white;
}

.signee-staff .signee-badge {
  background-color: #8b5cf6;
  color: white;
}

.signee-admin .signee-badge {
  background-color: #ef4444;
  color: white;
}

/* Mobile Optimization - 44x44pt minimum touch targets */
@media (max-width: 768px) {
  .signature-display {
    min-height: 44px;
    padding: 12px;
  }

  .signature-placeholder {
    font-size: 12px;
  }

  .signature-placeholder i {
    font-size: 24px;
  }
}
</style>
